/**
 * Breaking Free — A 30-Day Trauma Bond Recovery Journey
 *
 * Content mapped directly from Jana Burkhardt's book
 * "Breaking Free: Heal Trauma Bonds in 30 Days" (Recovery Trauma™).
 *
 * Nothing here diagnoses, scores or rates a member. There are no streaks and
 * no deadlines: "explored" is the only progress language used. Every member
 * response is private and is never read, analysed, translated or shared.
 */

export type Field = {
  key: string;
  label: string;
  placeholder?: string;
  rows?: number;
};

export type Block =
  | { type: "read"; key: string; title?: string; body: string[] }
  | {
      type: "questions";
      key: string;
      title: string;
      intro?: string;
      fields: Field[];
      closing?: string;
    }
  | {
      type: "selfCheck";
      key: string;
      title: string;
      intro?: string;
      items: string[];
      closing?: string;
    }
  | {
      type: "chips";
      key: string;
      title: string;
      intro?: string;
      options: string[];
      fields?: Field[];
      closing?: string;
    }
  | {
      type: "twoColumn";
      key: string;
      title: string;
      intro?: string;
      leftTitle: string;
      rightTitle: string;
      leftExamples: string[];
      rightExamples: string[];
      bodyOptions: string[];
      fields: Field[];
    }
  | {
      type: "bodyScan";
      key: string;
      title: string;
      intro?: string;
      closing?: string;
    }
  | { type: "triggerMap"; key: string; title: string; intro?: string }
  | {
      type: "cards";
      key: string;
      title: string;
      intro?: string;
      addLabel: string;
      fields: Field[];
      examples?: string[];
    }
  | {
      type: "affirmations";
      key: string;
      title: string;
      intro?: string;
      options: string[];
      closing?: string;
    }
  | {
      type: "steps";
      key: string;
      title: string;
      intro?: string;
      steps: { title: string; body: string; field?: Field }[];
      closing?: string;
    }
  | {
      type: "movement";
      key: string;
      title: string;
      intro?: string;
      options: { label: string; emoji: string; how: string }[];
      afterOptions: string[];
      closing?: string;
    }
  | {
      type: "futureSelf";
      key: string;
      title: string;
      intro?: string;
      fields: Field[];
      closing?: string;
    }
  | {
      type: "toolkit";
      key: string;
      title: string;
      intro?: string;
      examples: string[];
    };

export type RegulateKey =
  | "grounding"
  | "breathing"
  | "somatic"
  | "meditations"
  | "inner-child"
  | "trigger-journal"
  | "journal"
  | "affirmations"
  | "nervous-system-reset"
  | "regulation-compass";

export const REGULATE_LINKS: Record<
  RegulateKey,
  { to: string; label: string; emoji: string }
> = {
  grounding: { to: "/grounding", label: "Grounding", emoji: "🌱" },
  breathing: { to: "/breathing", label: "Breathing", emoji: "🌬️" },
  somatic: { to: "/somatic", label: "Somatic practice", emoji: "🧘" },
  meditations: { to: "/meditations", label: "Meditation", emoji: "🕊️" },
  "inner-child": { to: "/inner-child", label: "Inner Child work", emoji: "🧸" },
  "trigger-journal": { to: "/trigger-journal", label: "Trigger Journal", emoji: "📓" },
  journal: { to: "/journal", label: "Recovery Journal", emoji: "✍️" },
  affirmations: { to: "/affirmations", label: "Affirmations", emoji: "💛" },
  "nervous-system-reset": {
    to: "/nervous-system-reset",
    label: "Reset & Regulate",
    emoji: "🌊",
  },
  "regulation-compass": {
    to: "/regulation-compass",
    label: "Regulation Compass",
    emoji: "🧭",
  },
};

export type Phase = {
  key: string;
  name: string;
  range: string;
  from: number;
  to: number;
  blurb: string;
};

export const PHASES: Phase[] = [
  {
    key: "awareness",
    name: "Awareness",
    range: "Days 1–7",
    from: 1,
    to: 7,
    blurb:
      "Beginning to notice what has been happening inside you — your thoughts, your feelings, your triggers, and the story of how this began.",
  },
  {
    key: "cycle",
    name: "Breaking the Cycle",
    range: "Days 8–14",
    from: 8,
    to: 14,
    blurb:
      "Meeting the beliefs and patterns that kept the bond alive, and gently beginning to loosen their grip.",
  },
  {
    key: "resilience",
    name: "Building Resilience",
    range: "Days 15–21",
    from: 15,
    to: 21,
    blurb:
      "Affirmations, boundaries, self-compassion and inner child work — building the safety you may never have had.",
  },
  {
    key: "beginnings",
    name: "Embracing New Beginnings",
    range: "Days 22–30",
    from: 22,
    to: 30,
    blurb:
      "Reclaiming your body, your standards and your future — and writing the vision you are walking towards.",
  },
];

export function phaseForDay(day: number): Phase {
  return PHASES.find((p) => day >= p.from && day <= p.to) ?? PHASES[0];
}

/* ------------------------------------------------------------------ */
/* Before You Begin — Chapters 1–3                                     */
/* ------------------------------------------------------------------ */

export type IntroSection = {
  key: string;
  title: string;
  emoji: string;
  blocks: Block[];
  regulate?: RegulateKey[];
  safety?: string;
};

export const INTRO_SECTIONS: IntroSection[] = [
  {
    key: "welcome",
    title: "A word before you begin",
    emoji: "🕊️",
    blocks: [
      {
        type: "read",
        key: "welcome-read",
        body: [
          "We don't always realise we're caught in something until we try to leave it. Trauma bonds are not just difficult relationships — they are emotional chains, forged in cycles of pain, hope and confusion.",
          "This is not about blaming yourself or rushing your healing. It's about remembering who you are beneath the wounds, and gently guiding the parts of you that learned to cope, to protect and to please towards safety, self-trust and inner freedom.",
          "Healing won't be linear. You may feel resistance, grief, even guilt. That's okay. These feelings are not signs you're failing — they're proof that you're waking up.",
          "You are not alone. And you are not broken. You are brave for being here.",
          "— Jana, Founder, Recovery Trauma™",
        ],
      },
    ],
  },
  {
    key: "what-are-trauma-bonds",
    title: "What are trauma bonds?",
    emoji: "🔗",
    blocks: [
      {
        type: "read",
        key: "what-read",
        body: [
          "Trauma bonds are complicated emotional connections that develop between people, usually marked by a pattern of hurt followed by making up. They can form with romantic partners, family or friends.",
          "At the heart of trauma bonding is a combination of attachment and fear. People often feel pulled back towards someone who hurts them — because of shared experiences, moments of kindness, and the hope for change. That tug-of-war between mixed feelings creates a bond that can feel impossible to break.",
          "They can leave you feeling stuck, confused and emotionally exhausted, and make it hard to know what a healthy relationship even looks like.",
          "Understanding the cycle is the first step. Your brain's reward system learns that love has to be earned through pain — which is exactly why this isn't a matter of willpower, and never was a sign of weakness.",
        ],
      },
    ],
  },
  {
    key: "self-check",
    title: "Are you in a trauma bond?",
    emoji: "🤍",
    blocks: [
      {
        type: "selfCheck",
        key: "trauma-bond-self-check",
        title: "Take a moment to check in with yourself",
        intro: "Answer these honestly — no judgment, just reflection. Tap any that feel true.",
        items: [
          "Do you feel addicted to someone who repeatedly hurts you?",
          "Do you hope they'll change, even after multiple broken promises?",
          "Do you feel guilty for wanting to leave — like it's your fault things aren't working?",
          "Do you often blame yourself for their moods or actions?",
          "Are you constantly walking on eggshells, trying not to upset them?",
          "Do you feel more anxious than safe in the relationship?",
          "Do you miss the “good times” more than you feel peace in the present?",
        ],
        closing:
          "If three or more feel true, it may be a trauma bond — not love. You are not crazy. You are not weak. You're in a cycle that can be broken.",
      },
    ],
    safety:
      "This is a reflection, not a test or a diagnosis. Nothing here is scored, and nobody sees your answers.",
  },
  {
    key: "emotional-effects",
    title: "The emotional and psychological effects",
    emoji: "💭",
    blocks: [
      {
        type: "read",
        key: "emotional-read",
        body: [
          "The emotional impact of trauma bonds can run deep and last a long time. People often feel stuck in a loop of very high highs and very low lows, where closeness is mixed with fear and sadness. That rollercoaster can leave you confused and unsure about what is real. Those feelings are a normal reaction to what you've lived through.",
          "Guilt and shame often arrive too — believing it was somehow your fault, that you could have made things better. That self-blame is heavy, and noticing it is the first step to putting it down.",
          "Mindfulness helps here. Deep breathing, body scanning or writing can create a safe place for feelings to come out without judgment.",
        ],
      },
    ],
    regulate: ["breathing", "grounding"],
  },
  {
    key: "physical-signs",
    title: "The physical signs of trauma",
    emoji: "🫀",
    blocks: [
      {
        type: "read",
        key: "physical-read",
        body: [
          "Trauma isn't only something that happens in your head — it shows up in your body. Tension headaches, muscle aches, stomach problems, exhaustion. Your body reacts much like it does under threat: stress hormones rush in, leaving tension and pain behind.",
          "This isn't random. It's your body telling you something isn't right.",
          "Deep breathing, body scans and gentle movement help you get back in touch with your body and notice where it's holding tightness — not to fix it, but to listen to it.",
        ],
      },
    ],
    regulate: ["somatic", "breathing"],
    safety:
      "This is educational, not medical advice. If you have physical symptoms that worry you, please see a doctor.",
  },
  {
    key: "relationships",
    title: "How trauma bonds affect relationships",
    emoji: "🫂",
    blocks: [
      {
        type: "read",
        key: "relationships-read",
        body: [
          "Trauma bonds change how relationships work — often creating dependence and a great deal of emotional stress, where love and hurt are mixed together.",
          "Communication becomes defensive rather than honest, small misunderstandings grow, and the relationship starts to feel anxious and uncertain.",
          "Recovery takes a deliberate effort to notice the patterns. Awareness creates a space between the trigger and your reaction — and in that space, you get to choose.",
          "Boundaries act like a shield: they protect the room you need to heal.",
        ],
      },
    ],
  },
  {
    key: "intentions",
    title: "Setting intentions for change",
    emoji: "✨",
    blocks: [
      {
        type: "questions",
        key: "intentions",
        title: "What do you want this journey to give you?",
        intro:
          "Setting an intention isn't something you do once — you can come back and change these at any point.",
        fields: [
          {
            key: "improve",
            label: "What part of your life would you like to feel different?",
            rows: 3,
          },
          {
            key: "stopped",
            label: "What feelings or patterns have been stopping you from moving forward?",
            rows: 3,
          },
          { key: "instead", label: "How would you like to feel instead?", rows: 3 },
        ],
        closing: "Every intention you make is a step towards freeing yourself.",
      },
    ],
  },
  {
    key: "safe-space",
    title: "Creating a safe space for healing",
    emoji: "🏡",
    blocks: [
      {
        type: "read",
        key: "safe-space-read",
        body: [
          "A safe space lets you face your emotions without worrying about being judged. It can start with something physical — a quiet room, soft light, somewhere that feels like yours.",
          "Emotional safety matters just as much: people who respect the way you heal, and who listen.",
          "What helps one person may not help another. This is yours to design.",
        ],
      },
      {
        type: "questions",
        key: "safe-space",
        title: "Where will you do this work?",
        fields: [
          { key: "where", label: "Where do you feel most settled?", rows: 2 },
          {
            key: "helps",
            label: "What small things help that place feel safe (light, blanket, sound, door closed)?",
            rows: 3,
          },
        ],
      },
    ],
    regulate: ["grounding"],
  },
  {
    key: "support-network",
    title: "Building your support network",
    emoji: "🤝",
    blocks: [
      {
        type: "read",
        key: "support-read",
        body: [
          "Being around people who understand can give you strength. That might be friends, family, a support group, an online community, or a therapist who knows trauma recovery.",
          "It's completely okay to lean on others while you deal with this. Telling people how they can help you most — listening, company, practical help — makes it easier for them to show up.",
        ],
      },
      {
        type: "questions",
        key: "support-circle",
        title: "My Support Circle",
        intro: "Completely private. Nobody is contacted, and nobody sees this but you.",
        fields: [
          { key: "talk", label: "Who can I talk to?", rows: 2 },
          { key: "safe", label: "Who makes me feel emotionally safe?", rows: 2 },
          { key: "struggling", label: "Who can I contact when I'm struggling?", rows: 2 },
          {
            key: "professional",
            label: "What professional support do I have, or would I like?",
            rows: 2,
          },
        ],
      },
    ],
    safety:
      "If you are in danger or in crisis right now, please use the crisis support page — it lists immediate help.",
  },
];

/* ------------------------------------------------------------------ */
/* The 30 days                                                         */
/* ------------------------------------------------------------------ */

export type Day = {
  day: number;
  title: string;
  emoji: string;
  /** Today's focus — short educational explanation. */
  focus: string[];
  /** Pause & Notice — gentle check-in. */
  pause: string;
  /** Today's practice — the exercise from the book. */
  blocks: Block[];
  /** Regulate — existing Recovery Trauma™ practices. */
  regulate?: RegulateKey[];
  /** Today's reflection. */
  reflection: string;
  /** Take this with you. */
  affirmation: string;
  /** Extra care note shown before a heavier day begins. */
  tender?: boolean;
};

const journalPrompt = (
  key: string,
  title: string,
  fields: Field[],
  intro?: string,
): Block => ({ type: "questions", key, title, intro, fields });

export const DAYS: Day[] = [
  /* ---------------- Phase 1 — Awareness ---------------- */
  {
    day: 1,
    title: "Looking inward",
    emoji: "🌅",
    focus: [
      "Writing every day is one of the strongest ways to begin healing a trauma bond. It gives you somewhere to put thoughts and feelings without holding back — a safe place to think and to understand yourself better.",
      "Start with gentle questions that invite you to turn inward. Naming an emotion without judging it is what creates space for clarity and compassion.",
    ],
    pause: "Before you write — what is the temperature of today? Heavy, numb, restless, quiet?",
    blocks: [
      journalPrompt(
        "day1-journal",
        "Journal with me",
        [
          { key: "mind", label: "What thoughts are on my mind today?", rows: 4 },
          { key: "feelings", label: "What feelings am I holding right now?", rows: 4 },
          {
            key: "affected",
            label: "What happened today that affected me emotionally?",
            rows: 4,
          },
        ],
        "One question at a time. Everything saves as you write.",
      ),
    ],
    regulate: ["journal", "grounding"],
    reflection:
      "Every time you listen inwardly, you strengthen the connection to your true self — the one who is worthy, whole and healing.",
    affirmation: "Noticing is enough for today.",
  },
  {
    day: 2,
    title: "Where the body holds it",
    emoji: "🫁",
    focus: [
      "A body scan is one of the simplest mindfulness practices there is. You settle somewhere comfortable and move your attention slowly through your body, noticing sensation, tightness or discomfort.",
      "This keeps you in the present moment, and it shows you how trauma has been living in your body — not only in your thoughts.",
    ],
    pause: "Let your shoulders drop an inch. That's all for now.",
    blocks: [
      {
        type: "bodyScan",
        key: "day2-body-scan",
        title: "Body scan",
        intro:
          "Find somewhere you feel comfortable. We'll move slowly from head to feet. You can leave at any point.",
        closing:
          "You don't need to change the sensation. See if you can simply notice it for a moment.",
      },
    ],
    regulate: ["somatic", "breathing"],
    reflection: "Your body has been carrying this for a long time. Today you listened to it.",
    affirmation: "I can notice without fixing.",
  },
  {
    day: 3,
    title: "Noticing your triggers",
    emoji: "⚡",
    focus: [
      "Unhealed trauma often hides in the places where we feel most reactive. Knowing what sets off a strong feeling helps you build a space between what happens and how you respond.",
      "Throughout your day, gently observe when your emotions shift. Pause and ask: what just happened that made me feel this way? No need to fix it — just name it. That is awareness in action.",
    ],
    pause: "Think of one moment recently when something shifted inside you.",
    blocks: [
      {
        type: "triggerMap",
        key: "day3-trigger-map",
        title: "Trigger map",
        intro:
          "This saves straight into your own Trigger Journal, so everything stays in one place.",
      },
    ],
    regulate: ["trigger-journal", "regulation-compass", "grounding"],
    reflection:
      "Mapping your emotional landscape brings clarity to what once felt chaotic.",
    affirmation: "I am learning, and I'm proud of myself for noticing.",
  },
  {
    day: 4,
    title: "How it began",
    emoji: "🕯️",
    tender: true,
    focus: [
      "Let's start before everything became complicated. Think back to how the relationship began — what initially drew you in.",
      "We begin with the good, not the red flags. Maybe they were charming, warm, or made you feel seen in a way that touched something deep. You were drawn in for a reason, and that reason deserves to be seen and understood.",
    ],
    pause: "This one can bring up a lot. You can stop at any point and come back.",
    blocks: [
      journalPrompt("day4-story", "Telling the story of the relationship", [
        { key: "drew", label: "What initially drew you to this person?", rows: 4 },
        { key: "comforting", label: "What felt comforting, exciting or different?", rows: 4 },
        {
          key: "connected",
          label: "What were the first moments when you felt connected?",
          rows: 4,
        },
        { key: "seen", label: "What made you feel seen or understood?", rows: 4 },
        {
          key: "home",
          label: "Was there a part of you that felt “at home” with them?",
          rows: 4,
        },
      ]),
    ],
    regulate: ["grounding", "breathing"],
    reflection:
      "Pause here if you need to. You don't have to understand the whole relationship today.",
    affirmation: "There is no shame in having hoped.",
  },
  {
    day: 5,
    title: "What I needed from them",
    emoji: "💞",
    focus: [
      "Underneath the bond there is almost always an unmet need — the core longing that kept you holding on.",
      "This isn't about blame. It's about reclaiming your truth, so those needs can begin to be met in ways that are safe and gentle.",
    ],
    pause: "Put a hand where you feel this question land in your body.",
    blocks: [
      {
        type: "chips",
        key: "day5-needs",
        title: "What I needed most from them was…",
        intro: "Choose as many as feel true.",
        options: [
          "❤️ Love",
          "🛡️ Safety",
          "👁️ To feel seen",
          "🤍 Affection",
          "🏠 Stability",
          "💬 Reassurance",
          "🫂 Connection",
          "⭐ Validation",
          "🌱 Acceptance",
          "➕ Something else",
        ],
        fields: [
          {
            key: "words",
            label: "If you could put that need into words, what would it say?",
            rows: 4,
          },
          {
            key: "safely",
            label: "How could this need begin to be met safely today?",
            rows: 4,
          },
        ],
      },
    ],
    regulate: ["inner-child", "journal"],
    reflection: "Don't filter yourself. Say what you truly needed. It was never too much.",
    affirmation: "My needs were always reasonable.",
  },
  {
    day: 6,
    title: "Green flags and red flags",
    emoji: "🚩",
    focus: [
      "Let's get clear on what felt good — and what didn't. Green flags are what felt safe, kind or supportive. Red flags are what felt confusing, manipulative or painful.",
      "Listen to your body while you do this. It always remembers. This is how you start rebuilding trust in your own emotional compass.",
    ],
    pause: "Notice your breathing before you start. Slow it down by one count.",
    blocks: [
      {
        type: "twoColumn",
        key: "day6-flags",
        title: "What felt safe · What hurt or confused me",
        leftTitle: "🟢 What felt safe",
        rightTitle: "🔴 What hurt or confused me",
        leftExamples: ["They were affectionate.", "They said they loved me."],
        rightExamples: [
          "Affection disappeared when I disagreed with them.",
          "Love was sometimes used to control my behaviour.",
        ],
        bodyOptions: ["Calm", "Tight", "Heavy", "Anxious", "Numb", "Angry", "Sad", "Other"],
        fields: [
          {
            key: "notice",
            label: "Looking at both sides together, what do you notice now?",
            rows: 4,
          },
        ],
      },
    ],
    regulate: ["grounding", "breathing"],
    reflection: "Both columns can be true at once. That's exactly what made it so confusing.",
    affirmation: "I can trust what my body remembers.",
  },
  {
    day: 7,
    title: "Your patterns",
    emoji: "🔍",
    focus: [
      "At the end of the week, the book asks you to review what you recorded. Are there repeated triggers — people, places, situations? Are familiar emotions coming up again and again? What do these feelings remind you of?",
      "Uncovering patterns shows you where the pain started, and how to move forward with awareness.",
    ],
    pause: "Come back to your body first. A slow breath in, a slower one out.",
    blocks: [
      {
        type: "bodyScan",
        key: "day7-body-scan",
        title: "Body scan — where do you feel it?",
        intro:
          "Trauma lives in the body, not just the mind. Scan gently from head to toe. Place a hand on whatever feels activated, and simply breathe.",
      },
      journalPrompt("day7-patterns", "Looking for patterns", [
        {
          key: "repeated",
          label: "Are there repeated triggers — people, places, situations?",
          rows: 3,
        },
        { key: "familiar", label: "Which emotions keep coming up?", rows: 3 },
        { key: "remind", label: "What do these feelings remind you of?", rows: 3 },
        {
          key: "kind",
          label: "One kind sentence to yourself, after everything you've noticed this week",
          rows: 2,
        },
      ]),
    ],
    regulate: ["trigger-journal", "somatic"],
    reflection: "The goal isn't perfection. It's progress — with softness.",
    affirmation: "I deserve gentleness, even when I'm struggling.",
  },

  /* ---------------- Phase 2 — Breaking the Cycle ---------------- */
  {
    day: 8,
    title: "The beliefs underneath",
    emoji: "🪨",
    focus: [
      "Limiting beliefs usually come from past experiences, and they quietly shape how you see yourself. Naming them loosens their grip.",
      "Ask yourself: what do I say to myself when I struggle? Common ones sound like “I don't deserve love”, “I'll never succeed”, “I always mess things up”.",
    ],
    pause: "Notice whether you already feel defensive. That's information, not failure.",
    blocks: [
      {
        type: "cards",
        key: "day8-beliefs",
        title: "Beliefs I'm carrying",
        intro:
          "Add one belief at a time. These build your Beliefs I'm Leaving Behind and Beliefs I'm Practising lists.",
        addLabel: "Add a belief",
        examples: [
          "I don't deserve love.",
          "I'll never succeed.",
          "I always mess things up.",
          "I am difficult to love.",
        ],
        fields: [
          { key: "old", label: "Old belief", rows: 2 },
          { key: "learned", label: "Where might I have learned this?", rows: 3 },
          {
            key: "present",
            label: "Does this belong to my present, or could it come from an earlier experience?",
            rows: 3,
          },
          { key: "new", label: "What would a more compassionate belief sound like?", rows: 3 },
        ],
      },
    ],
    regulate: ["journal"],
    reflection:
      "These beliefs aren't facts. They're ideas formed by things that happened to you.",
    affirmation: "A thought is not a truth.",
  },
  {
    day: 9,
    title: "Mindfulness and reframing",
    emoji: "🌤️",
    focus: [
      "Mindfulness teaches you to notice your thoughts without immediately believing them — like watching clouds drift by instead of getting caught in the storm.",
      "That space between thought and reaction is where healing begins.",
    ],
    pause: "Three deep, slow breaths before you start. Let your body soften.",
    blocks: [
      {
        type: "steps",
        key: "day9-reframe",
        title: "A gentle four-step practice",
        steps: [
          {
            title: "Pause and breathe",
            body: "Take three deep, slow breaths. Inhale… exhale, and let your body soften.",
          },
          {
            title: "Name the thought",
            body: "Say to yourself: “I'm having the thought that…” This reminds you that a thought is just a thought.",
            field: { key: "thought", label: "I'm having the thought that…", rows: 2 },
          },
          {
            title: "Question it kindly",
            body: "Is this thought really true? Is this a belief I learned through pain or fear? Curiosity, not judgment.",
            field: { key: "question", label: "What comes up when you ask?", rows: 3 },
          },
          {
            title: "Reframe with compassion",
            body: "Replace the old thought with a grounded, gentle truth — for example: “I may feel unworthy right now, but I'm learning to value myself.”",
            field: { key: "reframe", label: "My kinder version", rows: 3 },
          },
        ],
      },
    ],
    regulate: ["breathing", "meditations"],
    reflection: "You're not denying your pain — you're choosing to see your strength within it.",
    affirmation: "I can notice a thought without obeying it.",
  },
  {
    day: 10,
    title: "Another way to look at it",
    emoji: "🔄",
    focus: [
      "When you catch a negative thought, pause and ask: is there another, more empowering way to look at this?",
      "Instead of “I'll never recover from this trauma”, try “I'm on a healing journey, and every day I'm becoming stronger.” It's a small shift that hands your power back.",
    ],
    pause: "What has your inner voice sounded like today — kind, flat, or harsh?",
    blocks: [
      {
        type: "cards",
        key: "day10-reframes",
        title: "Thoughts I'm reframing",
        addLabel: "Add a thought",
        fields: [
          { key: "old", label: "The thought as it arrives", rows: 2 },
          { key: "new", label: "A more grounded way to say it", rows: 2 },
        ],
      },
    ],
    reflection: "Repetition is how a new inner voice takes root. You're planting seeds.",
    affirmation: "I am learning to speak to myself differently.",
  },
  {
    day: 11,
    title: "Forgiveness — yourself, and them",
    emoji: "🤍",
    tender: true,
    focus: [
      "Forgiveness isn't saying what happened was okay, and it doesn't mean staying in contact. It's letting go of the weight of resentment so it stops costing you.",
      "The book suggests starting with yourself: recognising your own choices with kindness, understanding that everyone makes mistakes, and that you were surviving.",
    ],
    pause: "You can skip this day entirely and come back. Forgiveness cannot be rushed.",
    blocks: [
      journalPrompt("day11-forgiveness", "Two questions, no pressure", [
        { key: "self", label: "What do I need to forgive myself for?", rows: 4 },
        { key: "other", label: "Who do I need to forgive in order to move on?", rows: 4 },
        {
          key: "letter",
          label: "If you'd like: write the letter you'll never send (optional)",
          rows: 6,
        },
      ]),
    ],
    regulate: ["grounding", "journal"],
    reflection:
      "Forgiveness isn't a place you arrive at. It's something you keep choosing, for your own sake.",
    affirmation: "I can let go of the weight without excusing the harm.",
  },
  {
    day: 12,
    title: "Grieving who you hoped they'd be",
    emoji: "🌧️",
    tender: true,
    focus: [
      "Even if the relationship was painful, grief still shows up. You may mourn the version of them you believed in — the future you dreamed of — or the version of you who held on with hope.",
      "Grief isn't weakness. It's your heart accepting reality. And it's brave.",
    ],
    pause: "Tears are welcome here. So is numbness.",
    blocks: [
      journalPrompt("day12-grief", "Grief journal", [
        { key: "wished", label: "I am sad about the person I wished they could be…", rows: 3 },
        { key: "change", label: "I wanted them to change…", rows: 3 },
        { key: "future", label: "I thought we would have a future together…", rows: 3 },
        {
          key: "hopeful",
          label: "I grieve the part of me that stayed hopeful, even while hurting…",
          rows: 3,
        },
      ]),
    ],
    regulate: ["grounding", "meditations"],
    reflection: "Grief is love with nowhere to go. It deserves room, not a deadline.",
    affirmation: "My grief makes sense.",
  },
  {
    day: 13,
    title: "Space, contact and the urge",
    emoji: "📵",
    tender: true,
    focus: [
      "Unfollowing, muting or blocking isn't punishment — it's protection. Boundaries are not walls; they are doors to self-respect.",
      "Whether you've gone fully no contact or are somewhere in between, intense feelings can arise. You might want to text, check their profile, read old messages. Pause and ask what's underneath the urge.",
    ],
    pause: "If the urge is loud right now, ground first. The exercise will wait.",
    blocks: [
      journalPrompt("day13-detox", "Digital detox reflection", [
        { key: "felt", label: "How did it feel to set that boundary?", rows: 3 },
        {
          key: "resist",
          label: "Did any part of you resist? What thoughts came up?",
          rows: 3,
        },
      ]),
      journalPrompt("day13-contact", "No-contact check-in", [
        { key: "feeling", label: "What feelings am I having today? What's causing them?", rows: 3 },
        {
          key: "behind",
          label: "What's behind this urge — loneliness, fear, shame, the need to feel wanted?",
          rows: 3,
        },
      ]),
    ],
    regulate: ["grounding", "breathing", "regulation-compass"],
    reflection:
      "Most of the time, it isn't them you're missing — it's a wound that needs healing.",
    affirmation: "I can feel the pull without following it.",
  },
  {
    day: 14,
    title: "Shake it out",
    emoji: "🌀",
    focus: [
      "Trauma lives in the body — but so does the ability to let it go.",
      "Stand up. Play music if you'd like. For two minutes, shake your arms, legs, hips — like you're shaking off water. Then pause, hand on your heart or belly, and take three deep breaths. This is how your body completes the survival cycle.",
    ],
    pause: "If standing isn't available today, small movements count just as much.",
    blocks: [
      {
        type: "movement",
        key: "day14-shake",
        title: "Somatic release — shake and breathe",
        options: [
          { label: "Shake it out", emoji: "🌀", how: "Shake arms, legs and hips for two minutes — loose and messy is perfect." },
          { label: "Small movements", emoji: "🤲", how: "Shake just your hands and wrists, or rock gently where you are." },
          { label: "Sway to music", emoji: "🎵", how: "Put on one song and let your body move however it wants." },
        ],
        afterOptions: [
          "More settled",
          "More activated",
          "Emotional",
          "Energised",
          "Numb",
          "No change",
        ],
        closing: "There's no right answer here. However you feel afterwards is information, not a result.",
      },
      journalPrompt("day14-self-love", "Loving myself today looks like…", [
        { key: "love", label: "Loving myself today looks like…", rows: 3 },
      ]),
    ],
    regulate: ["somatic", "breathing"],
    reflection:
      "Self-love isn't always grand gestures. Sometimes it's a quiet no, a moment of rest, or a kind word.",
    affirmation: "Every small act of self-love is a piece of my healing.",
  },

  /* ---------------- Phase 3 — Building Resilience ---------------- */
  {
    day: 15,
    title: "Your own words",
    emoji: "💛",
    focus: [
      "Affirmations are short, supportive phrases that help rewire the way you speak to yourself — especially if trauma has made your inner voice harsh, scared or small.",
      "Choose one to three that feel meaningful today. Say them slowly, out loud if you can. Even if it feels awkward or untrue right now, say them anyway. You are planting seeds.",
    ],
    pause: "Notice any resistance to saying something kind about yourself. It's very common.",
    blocks: [
      {
        type: "affirmations",
        key: "my-healing-statements",
        title: "My Healing Statements",
        intro: "Save the ones that resonate, or write your own.",
        options: [
          "I am worthy of love and respect.",
          "I am healing, even if I can't see the progress yet.",
          "My voice matters.",
          "I am not defined by my past.",
          "I choose peace over chaos.",
          "I am safe in my body.",
          "I am learning to love myself.",
        ],
        closing:
          "These stay with you after Day 30, and your favourite can appear on your dashboard.",
      },
    ],
    regulate: ["affirmations"],
    reflection: "You're building a new inner voice — one that is kind, loving and true.",
    affirmation: "I am worthy of love and respect.",
  },
  {
    day: 16,
    title: "What boundaries actually are",
    emoji: "🧱",
    focus: [
      "Boundaries set up a structure for personal safety and emotional wellbeing. They let you decide what is okay and what isn't — a shield against the behaviours that hurt you.",
      "To begin, you need to know what you need and what you value. Think about the parts of your life that feel crossed or invaded, and the moments you felt uneasy or disrespected.",
    ],
    pause: "Where in your life do you already feel the word “no” waiting?",
    blocks: [
      journalPrompt("day16-boundaries-notice", "Where do I feel crossed?", [
        {
          key: "crossed",
          label: "Which parts of my life feel crossed or invaded right now?",
          rows: 4,
        },
        {
          key: "uneasy",
          label: "When have I felt uneasy or disrespected and said nothing?",
          rows: 4,
        },
        { key: "value", label: "What do I value most that I want to protect?", rows: 3 },
      ]),
    ],
    reflection: "You are allowed to say no. You are allowed to ask for space.",
    affirmation: "Protecting my peace is not unkind.",
  },
  {
    day: 17,
    title: "Saying it out loud",
    emoji: "🗣️",
    focus: [
      "Setting a boundary isn't mean — it's honest self-respect. The book gives you sentence starters to practise with: “When [this happens], I feel [emotion]. What I need is [clear request].”",
      "Practising in writing is a powerful first step. With repetition it becomes easier to say out loud, calmly and clearly.",
    ],
    pause: "Guilt may show up while you write. Let it sit beside you rather than stop you.",
    blocks: [
      {
        type: "cards",
        key: "my-boundaries",
        title: "My Boundaries",
        intro: "Each boundary gets its own card. You can come back and edit them any time.",
        addLabel: "Add a boundary",
        examples: [
          "When you interrupt me, I feel dismissed. I need space to finish my thoughts.",
          "I'm not comfortable texting late at night. Let's talk during the day instead.",
          "When plans change last minute, I feel anxious. Please give me more notice.",
        ],
        fields: [
          { key: "crosses", label: "What crosses my boundary?", rows: 2 },
          { key: "need", label: "What do I need instead?", rows: 2 },
          { key: "say", label: "What could I say?", rows: 3 },
          { key: "difficult", label: "What makes this boundary difficult to hold?", rows: 2 },
        ],
      },
    ],
    regulate: ["journal"],
    reflection: "You are allowed to protect your peace — without apology.",
    affirmation: "My needs can be spoken.",
  },
  {
    day: 18,
    title: "Talking to yourself like a friend",
    emoji: "🫶",
    focus: [
      "Many survivors struggle to be kind to themselves — blaming themselves for the pain, or believing they don't deserve gentleness.",
      "Self-compassion is learning to talk to yourself the way you would to a dear friend: with honesty, kindness and care.",
    ],
    pause: "Think of a recent moment where you were hard on yourself.",
    blocks: [
      {
        type: "steps",
        key: "day18-compassion",
        title: "Practising self-compassion",
        steps: [
          {
            title: "Notice the inner critic",
            body: "Examples from the book: “I should be over this by now.” “It's my fault I let them treat me that way.” “I'm too damaged to be loved.”",
            field: { key: "critic", label: "The critical thought I had", rows: 3 },
          },
          {
            title: "Respond with compassion",
            body: "Imagine a friend came to you with that same thought. How would you answer them?",
            field: { key: "friend", label: "What I would say to them", rows: 4 },
          },
          {
            title: "Turn it into an affirmation",
            body: "Something you can say when the inner critic gets loud. It's okay if it feels awkward at first.",
            field: { key: "affirmation", label: "My self-compassion affirmation", rows: 2 },
          },
        ],
        closing: "This saves into My Healing Statements too.",
      },
    ],
    regulate: ["affirmations"],
    reflection:
      "You stayed because you were trying to survive. That doesn't make you weak — it makes you human.",
    affirmation: "I don't need to be perfect to be worthy.",
  },
  {
    day: 19,
    title: "Little me",
    emoji: "🧸",
    tender: true,
    focus: [
      "Trauma bonds often form because a wounded part of us — usually our inner child — is still searching for love, safety or validation that was missing early on.",
      "Healing means connecting with that younger self and giving them what they never received: consistent love, emotional safety, and the sense of being enough.",
    ],
    pause: "Picture yourself at a younger age — maybe five, maybe twelve.",
    blocks: [
      journalPrompt("day19-little-me", "Little me needed to feel…", [
        { key: "needed", label: "What did that version of you need most?", rows: 4 },
      ], "Examples from the book: safe · seen · loved · protected · wanted · someone to stay · to be believed."),
      journalPrompt("day19-safe-space", "Draw or imagine your safe space", [
        { key: "smell", label: "What does it smell like?", rows: 2 },
        { key: "sounds", label: "What sounds do you hear?", rows: 2 },
        { key: "colours", label: "What colours surround you?", rows: 2 },
        { key: "who", label: "Who or what is allowed there?", rows: 2 },
        { key: "protected", label: "What are you protected from?", rows: 2 },
      ], "Real or imagined. Your brain responds to imagined safety too — return here whenever you feel overwhelmed."),
    ],
    regulate: ["inner-child", "meditations"],
    reflection: "Let your truth pour out. There is no shame in your needs. There never was.",
    affirmation: "Little me deserved better, and I can give it now.",
  },
  {
    day: 20,
    title: "Your regulation kit",
    emoji: "🧺",
    focus: [
      "The book asks you to build a self-soothing kit — a small box or pouch filled with calming things: a soft fabric or grounding stone, calming oils, a playlist, herbal tea, a candle, a note from your wiser self, affirmation cards.",
      "Use it whenever your nervous system needs tenderness, not judgment.",
    ],
    pause: "What has actually helped you settle in the last week? Start there.",
    blocks: [
      {
        type: "cards",
        key: "my-regulation-kit",
        title: "My regulation kit",
        addLabel: "Add something to my kit",
        examples: [
          "A soft fabric or grounding stone",
          "Calming essential oils",
          "A playlist of comforting music",
          "Herbal tea or a scented candle",
          "A note from my future, wiser self",
        ],
        fields: [
          { key: "item", label: "What goes in the kit", rows: 2 },
          { key: "why", label: "When would I reach for it?", rows: 2 },
        ],
      },
      {
        type: "affirmations",
        key: "my-safety-affirmations",
        title: "Safety affirmations",
        intro: "Choose or write five that speak to your need for safety and enoughness.",
        options: [
          "I am safe in my body.",
          "I do not have to earn love.",
          "It is safe to rest.",
          "My worth is not up for debate.",
          "I trust myself to choose peace.",
        ],
      },
    ],
    regulate: ["nervous-system-reset", "grounding"],
    reflection: "Tenderness, not judgment. That's what the kit is for.",
    affirmation: "It is safe to rest.",
  },
  {
    day: 21,
    title: "I see you",
    emoji: "🪞",
    tender: true,
    focus: [
      "Mirror work: stand in front of a mirror, place your hand over your heart, look gently into your own eyes and say — “I see you.” “I believe you.” “I'm here with you.” “I won't abandon you again.” “You are safe now.”",
      "Then the reparenting letter: writing to the child in you who stayed loyal to pain. You are not abandoning them. You're becoming the parent they needed.",
    ],
    pause: "If the mirror feels like too much today, say the words with your eyes closed.",
    blocks: [
      journalPrompt("day21-safety", "What does emotional safety feel like?", [
        { key: "when", label: "When, if ever, have I felt truly safe?", rows: 3 },
        { key: "body", label: "What did it feel like in my body?", rows: 3 },
        { key: "allowed", label: "What allowed that safety to exist?", rows: 3 },
        {
          key: "imagine",
          label: "If you've never felt it — what do you imagine it might feel like?",
          rows: 3,
        },
      ]),
      journalPrompt("day21-reparenting", "Reparenting letter", [
        {
          key: "letter",
          label: "To the child in me who stayed loyal to pain…",
          rows: 10,
          placeholder:
            "I'm so sorry you had to carry so much. You did your best to protect us. But I know now. I choose better for us.",
        },
      ]),
    ],
    regulate: ["inner-child", "grounding"],
    reflection: "Let these words reach the part of you that still waits to be seen.",
    affirmation: "I won't abandon myself again.",
  },

  /* ---------------- Phase 4 — New Beginnings ---------------- */
  {
    day: 22,
    title: "The protective shield",
    emoji: "🛡️",
    focus: [
      "Visualisation builds a safe mental space where you can look at your feelings from a steadier place. It moves you from being at the mercy of what happened to taking part in your own healing.",
      "Picture a protective shield around you — a bright light giving off warmth and safety. Whenever things feel like too much, you can return to this image.",
    ],
    pause: "Three slow breaths first. Let the exhale be longer than the inhale.",
    blocks: [
      {
        type: "steps",
        key: "day22-shield",
        title: "A guided shield practice",
        steps: [
          { title: "Settle", body: "Sit comfortably. Feet on the floor. Three slow breaths." },
          {
            title: "See the light",
            body: "Imagine a warm light beginning at your chest and spreading outwards until it surrounds you completely.",
            field: { key: "colour", label: "What colour is your shield?", rows: 1 },
          },
          {
            title: "Set what it holds out",
            body: "This shield doesn't shut the world away. It keeps out what harms you.",
            field: { key: "keepsout", label: "What does it keep out?", rows: 2 },
          },
          {
            title: "Set what it keeps safe",
            body: "And it protects what matters.",
            field: { key: "keepssafe", label: "What does it keep safe?", rows: 2 },
          },
        ],
        closing: "You can return to this image any time. That's the point of it.",
      },
    ],
    regulate: ["meditations", "breathing"],
    reflection: "You have the power to hold your emotions without them taking over.",
    affirmation: "I can protect myself and still stay open.",
  },
  {
    day: 23,
    title: "New patterns I'm building",
    emoji: "🌿",
    focus: [
      "Real healing happens in gentle, consistent steps. Pick one habit that might help you feel a little safer or more cared for. Then make it so small it's almost too easy to fail — one sentence twice a week, not thirty minutes a day.",
      "Link it to something you already do: “After I brush my teeth, I will take three deep breaths.” That's habit stacking, and it's what makes it stick.",
    ],
    pause: "No streaks here. Returning to an old pattern is information, never failure.",
    blocks: [
      {
        type: "cards",
        key: "new-patterns",
        title: "New Patterns I'm Building",
        intro: "Old pattern → what I notice → what I want to try instead.",
        addLabel: "Add a pattern",
        examples: [
          "I want to start stretching for five minutes each morning because it will help me feel more grounded and calm.",
        ],
        fields: [
          { key: "old", label: "Old pattern", rows: 2 },
          { key: "notice", label: "What I notice when it happens", rows: 2 },
          { key: "instead", label: "What I want to try instead", rows: 2 },
          { key: "when", label: "What I'll link it to (after I…)", rows: 1 },
        ],
      },
    ],
    regulate: ["breathing", "journal"],
    reflection:
      "Some days you'll fall back into old ways. That's part of it — not a setback, a chance to learn.",
    affirmation: "Small wins build self-trust.",
  },
  {
    day: 24,
    title: "Celebrating what's changed",
    emoji: "🌟",
    focus: [
      "Celebrating your progress helps you see how much you've grown, even when it feels tiny. Every step — noticing the bond, practising mindfulness, holding one boundary — is worth recognising.",
      "Have you begun to set boundaries? Have you started to see the patterns? Each realisation is a step forward.",
    ],
    pause: "Read back something you wrote in week one before you answer.",
    blocks: [
      journalPrompt("day24-celebrate", "What has shifted?", [
        { key: "changed", label: "What has changed since I started?", rows: 4 },
        { key: "noticing", label: "What am I noticing now that I didn't before?", rows: 4 },
        { key: "proud", label: "What am I quietly proud of?", rows: 4 },
      ]),
    ],
    reflection: "Every little bit of progress is a win that deserves to be celebrated.",
    affirmation: "I am allowed to be proud of small things.",
  },
  {
    day: 25,
    title: "My non-negotiables",
    emoji: "📜",
    focus: [
      "Boundaries are no longer a wall — they're a bridge to safety and self-respect.",
      "This is your inner contract: your new standard of love and safety.",
    ],
    pause: "Answer as the person you are becoming, not the person you had to be.",
    blocks: [
      journalPrompt("day25-non-negotiables", "What are my non-negotiables now?", [
        {
          key: "tolerate",
          label: "What behaviours will I no longer tolerate — from others, or from myself?",
          rows: 4,
        },
        {
          key: "need",
          label: "What do I need to feel safe, calm and emotionally respected?",
          rows: 4,
        },
        { key: "contract", label: "My new non-negotiables", rows: 5 },
      ]),
    ],
    reflection: "This is your inner contract — your new standard of love and safety.",
    affirmation: "My standards protect me.",
  },
  {
    day: 26,
    title: "The unsent letter",
    emoji: "✉️",
    tender: true,
    focus: [
      "This letter is not for them. It's for you — to clear space inside your heart.",
      "Write what you never got to say. When you're done you can keep it, or delete it. It's yours either way.",
    ],
    pause:
      "This is one of the heaviest days. Ground first if you need to, and leave it half-written if that's what today allows.",
    blocks: [
      journalPrompt("day26-letter", "To the person I was trauma bonded to", [
        {
          key: "letter",
          label: "Write what you never got to say",
          rows: 14,
          placeholder:
            "You hurt me. I loved you more than I loved myself. I waited for you to change — but I'm the one who finally did.",
        },
      ]),
    ],
    regulate: ["grounding", "breathing"],
    reflection: "Keep it as proof of your courage, or let it go. Both are complete.",
    affirmation: "My words belong to me now.",
  },
  {
    day: 27,
    title: "Reclaiming",
    emoji: "🕊️",
    focus: [
      "Even in heartbreak, something beautiful begins. The book asks you to list five things you've reclaimed since letting go — your voice, your sleep, your peace, your boundaries, your joy.",
      "Then choose one gentle movement today, and as you move, whisper: “This body is mine.” “I am safe now.”",
    ],
    pause: "You didn't lose yourself — you found yourself.",
    blocks: [
      {
        type: "cards",
        key: "day27-reclaiming",
        title: "Five things I'm reclaiming",
        addLabel: "Add something I'm reclaiming",
        examples: ["My voice", "My sleep", "My peace", "My boundaries", "My joy"],
        fields: [{ key: "item", label: "What I'm reclaiming", rows: 1 }],
      },
      {
        type: "movement",
        key: "day27-movement",
        title: "How would your body like to move today?",
        options: [
          { label: "Slow walk", emoji: "🚶", how: "Walk slowly, noticing how your feet meet the ground." },
          { label: "Gentle stretch", emoji: "🤸", how: "Stretch your arms wide. Let your breath follow the movement." },
          { label: "Sway to music", emoji: "🎵", how: "Put on your favourite song and let your body sway however it wants." },
        ],
        afterOptions: [
          "More settled",
          "More activated",
          "Emotional",
          "Energised",
          "Numb",
          "No change",
        ],
        closing: "This exercise doesn't have to make you feel better. It just has to be yours.",
      },
    ],
    regulate: ["somatic"],
    reflection: "This body is mine. I get to feel peace and pleasure again.",
    affirmation: "I am reclaiming myself, piece by piece.",
  },
  {
    day: 28,
    title: "What I've learned about myself",
    emoji: "📖",
    focus: [
      "Let the pain speak of your strength — not your shame.",
      "Your wounds carry wisdom. This is where healing begins.",
    ],
    pause: "One question at a time. There's no hurry.",
    blocks: [
      journalPrompt("what-im-learning", "Three questions", [
        { key: "strengths", label: "What strengths did I uncover?", rows: 4 },
        { key: "truths", label: "What truths did I finally face?", rows: 4 },
        { key: "parts", label: "What parts of me asked to be seen?", rows: 4 },
      ]),
    ],
    reflection:
      "Your answers become your private page, What I'm Learning About Myself — built only from your own words.",
    affirmation: "My pain taught me something about my strength.",
  },
  {
    day: 29,
    title: "Today, I choose myself",
    emoji: "🕯️",
    focus: [
      "The book calls this a daily vow — a whisper of self-loyalty. “Today, I choose myself by resting.” “By not replying to the message.” “By holding my boundary.” “By being kind to my inner child.” “By trusting my healing.”",
      "In the evening, if you'd like: light a candle, close your eyes, and whisper your own name like a prayer — “I see you. I will not abandon you again.”",
    ],
    pause: "Small counts. Quiet counts.",
    blocks: [
      {
        type: "cards",
        key: "choosing-myself",
        title: "Today, I choose myself by…",
        intro:
          "This stays with you after Day 30 — over time it becomes Ways I've Been Choosing Myself.",
        addLabel: "Add today's commitment",
        examples: [
          "By resting.",
          "By not replying to the message.",
          "By holding my boundary.",
          "By being kind to my inner child.",
          "By trusting my healing.",
        ],
        fields: [{ key: "commitment", label: "Today, I choose myself by…", rows: 2 }],
      },
      journalPrompt("day29-ritual", "Sacred ritual (optional)", [
        {
          key: "ritual",
          label: "If you did the candle ritual — what did it feel like to say your own name with love?",
          rows: 4,
        },
      ]),
    ],
    reflection: "Recommit to you — every day, in small, sacred ways.",
    affirmation: "I will not leave myself behind again.",
  },
  {
    day: 30,
    title: "A day in the life of future me",
    emoji: "🌅",
    focus: [
      "Close your eyes for a moment. Take a few slow breaths. Now imagine a future version of you — grounded, calm, free, no longer needing to shrink or explain.",
      "This isn't a fantasy and it isn't a prediction. It's your blueprint — a soft, powerful map to return to whenever you forget where you're headed.",
    ],
    pause: "Breathe first. Then answer one question at a time.",
    blocks: [
      {
        type: "futureSelf",
        key: "my-future-self",
        title: "A Day in the Life of Future Me",
        intro: "Your answers come together into one piece you can read again and edit.",
        fields: [
          { key: "wake", label: "Where do you wake up?", rows: 3 },
          { key: "body", label: "How does your body feel?", rows: 3 },
          { key: "day", label: "How do you move through your day?", rows: 3 },
          { key: "who", label: "Who is around you?", rows: 3 },
          { key: "relationships", label: "How do those relationships feel?", rows: 3 },
          {
            key: "peace",
            label: "What does peace feel like in your nervous system?",
            rows: 3,
          },
          { key: "proud", label: "What are you proud of?", rows: 3 },
          { key: "grateful", label: "What are you grateful for?", rows: 3 },
        ],
        closing: "Keep this vision close. You're becoming her — one step at a time.",
      },
      journalPrompt("day30-love-letter", "A love letter to yourself", [
        {
          key: "letter",
          label: "Write to yourself as your own best friend",
          rows: 10,
          placeholder:
            "I am so proud of you. You didn't give up. You chose truth even when it hurt, and you kept showing up for yourself. You are not broken — you are becoming.",
        },
      ]),
    ],
    regulate: ["breathing", "meditations"],
    reflection:
      "Healing is not about becoming someone new — it's about coming home to who you've always been beneath the pain.",
    affirmation: "I am not where I started.",
  },
];

export function dayByNumber(n: number): Day | undefined {
  return DAYS.find((d) => d.day === n);
}

/* ------------------------------------------------------------------ */
/* Weekly reflections                                                  */
/* ------------------------------------------------------------------ */

export const WEEK_REFLECTION_FIELDS: Field[] = [
  { key: "noticed", label: "What did you notice this week?", rows: 3 },
  { key: "difficult", label: "What felt difficult?", rows: 3 },
  { key: "surprised", label: "What surprised you?", rows: 3 },
  { key: "proud", label: "What are you proud of?", rows: 3 },
  { key: "carry", label: "What would you like to carry into the next phase?", rows: 3 },
];

export const WEEK_REFLECTION_DAYS = [7, 14, 21, 30];

/* ------------------------------------------------------------------ */
/* After Day 30 — Your Journey Continues (Chapters 8 & 9)              */
/* ------------------------------------------------------------------ */

export const CLOSING_BLOCKS: Block[] = [
  {
    type: "questions",
    key: "my-breaking-free-reflection",
    title: "My Breaking Free Reflection",
    intro: "Comparing where you began with where you are now. One line at a time.",
    fields: [
      { key: "started", label: "When I started this journey…", rows: 3 },
      { key: "understand", label: "Today I understand…", rows: 3 },
      { key: "recognise", label: "I now recognise…", rows: 3 },
      { key: "protect", label: "I want to protect…", rows: 3 },
      { key: "trust", label: "I am learning to trust…", rows: 3 },
      { key: "future", label: "I want future relationships to feel…", rows: 3 },
      {
        key: "biggest",
        label: "The biggest thing I learned about myself is…",
        rows: 3,
      },
    ],
  },
  {
    type: "questions",
    key: "who-am-i-becoming",
    title: "Who Am I Becoming?",
    intro: "Your identity is not decided by what happened to you.",
    fields: [
      { key: "rediscovering", label: "What parts of myself am I rediscovering?", rows: 3 },
      { key: "enjoy", label: "What do I enjoy?", rows: 3 },
      { key: "matters", label: "What matters to me now?", rows: 3 },
      { key: "more", label: "What do I want more of?", rows: 3 },
      { key: "no-longer", label: "What do I no longer want to carry?", rows: 3 },
      { key: "i-am", label: "I am more than what happened to me. I am…", rows: 4 },
    ],
  },
  {
    type: "cards",
    key: "my-intentions",
    title: "Gentle intentions",
    intro:
      "A few soft intentions for what comes next — relationships, self-care, boundaries, friendships, interests, growth. Intentions, never demands.",
    addLabel: "Add an intention",
    fields: [
      { key: "area", label: "Area (relationships, self-care, boundaries…)", rows: 1 },
      { key: "intention", label: "My intention", rows: 2 },
    ],
  },
  {
    type: "toolkit",
    key: "my-regulation-toolkit",
    title: "My Personal Regulation Toolkit",
    intro:
      "Built from the practices you found along the way. When I feel this → I reach for that.",
    examples: [
      "When I'm anxious → Extended exhale breathing",
      "When I'm dissociating → Grounding",
      "When my body feels activated → Somatic practice",
      "When I want to contact them → Pause and journal first",
    ],
  },
  {
    type: "cards",
    key: "things-that-help-me",
    title: "Things That Help Me Feel Like Me",
    intro:
      "Not “have a bath” — whatever genuinely supports you. Activities, rest, people, places, music.",
    addLabel: "Add something that helps",
    fields: [{ key: "thing", label: "What helps me feel like me", rows: 2 }],
  },
];

/* ------------------------------------------------------------------ */
/* My Breaking Free Journey — the private collection page              */
/* ------------------------------------------------------------------ */

export const JOURNEY_SECTIONS: { key: string; title: string; emoji: string }[] = [
  { key: "day3-trigger-map", title: "My Triggers & Patterns", emoji: "⚡" },
  { key: "day4-story", title: "My Relationship Story", emoji: "🕯️" },
  { key: "day5-needs", title: "What I Needed", emoji: "💞" },
  { key: "day6-flags", title: "My Red & Green Flags", emoji: "🚩" },
  { key: "day2-body-scan", title: "What My Body Tells Me", emoji: "🫁" },
  { key: "day8-beliefs", title: "Beliefs I'm Leaving Behind & Practising", emoji: "🪨" },
  { key: "my-healing-statements", title: "My Affirmations", emoji: "💛" },
  { key: "my-boundaries", title: "My Boundaries", emoji: "🧱" },
  { key: "new-patterns", title: "New Patterns I'm Building", emoji: "🌿" },
  { key: "choosing-myself", title: "Ways I'm Choosing Myself", emoji: "🕯️" },
  { key: "what-im-learning", title: "What I've Learned About Myself", emoji: "📖" },
  { key: "my-future-self", title: "My Future Self", emoji: "🌅" },
  { key: "my-regulation-toolkit", title: "My Personal Regulation Toolkit", emoji: "🧭" },
  { key: "support-circle", title: "My Support Circle", emoji: "🤝" },
  { key: "things-that-help-me", title: "Things That Help Me Feel Like Me", emoji: "🌼" },
];

export const SAFETY_NOTICE =
  "Breaking Free is educational self-help support. It is not therapy, diagnosis, medical advice or emergency care. If an exercise becomes overwhelming, please stop, ground yourself, and come back when you feel ready.";

export const NO_STREAK_NOTICE =
  "There is no deadline and no streak. “30 days” describes the structure, not a schedule. Your journey is still here whenever you're ready.";
